import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Navbar } from '../components/layout/Navbar';
import { apiFetch } from '../lib/apiClient';
import { 
  Upload, 
  Image as ImageIcon, 
  X, 
  CheckCircle2, 
  Sparkles,
  Eye,
  Palette,
  Layers,
  Lightbulb,
  ArrowRight,
  Plus,
  AlertCircle
} from 'lucide-react';

export default function UploadPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [analysisError, setAnalysisError] = useState('');
  const [analysisData, setAnalysisData] = useState(null);
  const [isCheckingProfile, setIsCheckingProfile] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const checkProfile = async () => {
      try {
        const response = await apiFetch('/auth/profile', { method: 'GET' });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data?.message || data?.error || 'Unable to load profile');
        }

        const profile = data?.profile || data;
        const hasSkillGapReport = Boolean(profile?.skillGapReport && Object.keys(profile.skillGapReport).length > 0);
        const hasRoadmap = Boolean(profile?.currentRoadmap && Object.keys(profile.currentRoadmap).length > 0);

        if (hasRoadmap && isMounted) {
          navigate('/dashboard', { replace: true });
          return;
        }
      } catch {
        // If profile fetch fails, still allow the user to upload reference art.
      } finally {
        if (isMounted) {
          setIsCheckingProfile(false);
        }
      }
    };

    checkProfile();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  const buildUiAnalysis = (skillGapReport) => {
    const metrics = skillGapReport?.skillMetrics || {};
    const entries = Object.entries(metrics);

    if (!entries.length) {
      return null;
    }

    const averageScore =
      entries.reduce((sum, [, val]) => sum + Number(val || 0), 0) / entries.length;

    let skillLevel = 'Beginner';
    if (averageScore >= 8) skillLevel = 'Advanced';
    else if (averageScore >= 6) skillLevel = 'Upper Intermediate';
    else if (averageScore >= 4) skillLevel = 'Intermediate';

    const categories = entries.map(([name, score]) => ({
      name,
      score: Number(score) * 10,
      feedback: '',
    }));

    return {
      overallScore: Math.round(averageScore * 10),
      skillLevel,
      categories,
      strengths: skillGapReport.identifiedGaps || [],
      improvements: [],
      recommendedTrack: '',
    };
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/') && file.size <= 10 * 1024 * 1024
    );
    handleFiles(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files).filter(file => 
      file.type.startsWith('image/') && file.size <= 10 * 1024 * 1024
    );
    handleFiles(files);
  };

  const handleFiles = (files) => {
    if (!files.length) return;

    const file = files[0];
    const image = {
      id: Math.random().toString(36).substr(2, 9),
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
    };

    // Limit to a single artwork for analysis
    setUploadedImages([image]);
    setAnalysisComplete(false);
    setAnalysisError('');
  };

  const removeImage = (id) => {
    setUploadedImages(prev => prev.filter(img => img.id !== id));
    setAnalysisComplete(false);
  };

  const [isGeneratingRoadmap, setIsGeneratingRoadmap] = useState(false);

  const handleAnalyze = async () => {
    if (!uploadedImages.length) return;

    const image = uploadedImages[0];
    const file = image.file;

    if (!file) return;

    setAnalysisError('');
    setIsAnalyzing(true);
    setIsGeneratingRoadmap(false);

    try {
      // 1. Get pre-signed upload URL from ExerciseLambda
      const exerciseId = 'reference-art';
      const uploadResponse = await apiFetch(`/exercise/${exerciseId}/upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          filename: file.name,
          contentType: file.type || 'image/jpeg',
        }),
      });

      const uploadData = await uploadResponse.json().catch(() => ({}));

      if (!uploadResponse.ok || !uploadData.uploadUrl || !uploadData.s3Key) {
        const message =
          uploadData?.message ||
          uploadData?.error ||
          'Could not get upload link. Please try again.';
        throw new Error(message);
      }

      const { uploadUrl, s3Key } = uploadData;
      console.log('uploadUrl', uploadUrl);
      console.log('s3Key', s3Key);

      // 2. Upload image directly to S3 using pre-signed URL
      const s3Response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type || 'image/jpeg',
        },
        body: file,
      });

      if (!s3Response.ok) {
        throw new Error('Failed to upload artwork. Please try again.');
      }

      console.log('s3Response', s3Response);

      // 3. Trigger diagnostic image analysis with S3 link
      const artworkUrl = `s3://${s3Key}`;

      const analyzeResponse = await apiFetch('/diagnostic/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ artworkUrl }),
      });

      console.log('analyzeResponse', analyzeResponse);

      const analyzeData = await analyzeResponse.json().catch(() => ({}));

      if (!analyzeResponse.ok) {
        const message =
          analyzeData?.message ||
          analyzeData?.error ||
          'Image analysis failed. Please try again.';
        throw new Error(message);
      }

      console.log('analyzeData', analyzeData);

      const uiAnalysis = buildUiAnalysis(analyzeData.skillGapReport);
      setAnalysisData(uiAnalysis);

      const jobId = analyzeData.roadmapJobId || analyzeData.jobId;
      if (jobId) {
        setIsGeneratingRoadmap(true);
        let status = 'PROCESSING';
        while (status !== 'COMPLETED') {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          const statusRes = await apiFetch(`/roadmap/generate/status?jobId=${jobId}`);
          const statusData = await statusRes.json().catch(() => ({}));
          
          if (statusRes.ok && statusData.status) {
            status = statusData.status;
            if (status === 'FAILED') {
              throw new Error('Roadmap generation failed. Please try again.');
            }
          } else {
             throw new Error('Failed to verify roadmap status.');
          }
        }
      }

      sessionStorage.removeItem('sidebar_profile');
      window.dispatchEvent(new Event('profileUpdate'));

      setAnalysisComplete(true);
    } catch (err) {
      setAnalysisError(err.message || 'Something went wrong. Please try again.');
      setAnalysisComplete(false);
    } finally {
      setIsAnalyzing(false);
      setIsGeneratingRoadmap(false);
    }
  };

  const handleContinue = () => {
    navigate('/dashboard');
  };

  if (isCheckingProfile) {
    return (
      <div className="min-h-screen bg-[hsl(220,20%,97%)] flex flex-col" data-testid="upload-loading">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center p-8 mt-12 animate-in fade-in duration-500">
          <div className="relative w-24 h-24 mb-6">
            <div className="absolute inset-0 bg-[hsl(234,89%,63%)]/20 rounded-full blur-xl animate-pulse"></div>
            <div className="absolute inset-0 rounded-full border-4 border-slate-200"></div>
            <div className="absolute inset-0 rounded-full border-4 border-[hsl(234,89%,63%)] border-t-transparent animate-spin duration-1000"></div>
            <div className="absolute inset-0 flex items-center justify-center bg-white rounded-full m-2 shadow-sm">
              <ImageIcon className="w-8 h-8 text-[hsl(234,89%,63%)] animate-pulse" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-[hsl(222,47%,11%)] font-['Outfit'] mb-2">
            Loading your roadmap
          </h2>
          <p className="text-slate-500 max-w-sm text-center animate-pulse">
            Checking your personalized curriculum and learning path...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(220,20%,97%)]" data-testid="upload-page">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 animate-fade-in">
            <div className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-sm border border-slate-200/60 mb-4">
              <ImageIcon className="w-4 h-4 text-[hsl(234,89%,63%)]" />
              <span className="text-sm font-medium text-slate-600">Reference Art Analysis</span>
            </div>
            <h1 className="text-3xl font-bold text-[hsl(222,47%,11%)] mb-3 font-['Outfit']">
              Upload Your Artwork
            </h1>
            <p className="text-base text-slate-600 max-w-xl mx-auto">
              Share your reference art for AI-powered skill gap analysis. We'll evaluate technique and generate your personalized curriculum.
            </p>
          </div>

          {/* Upload Area */}
          {!analysisComplete && (
            <Card className="card-elevated border-0 mb-8" data-testid="upload-card">
              <CardContent className="p-6">
                {/* Drop Zone */}
                <div
                  className={`upload-zone p-8 text-center cursor-pointer ${isDragging ? 'drag-over' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="drop-zone"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    className="hidden"
                    onChange={handleFileSelect}
                    data-testid="file-input"
                  />
                  
                  <div className="w-14 h-14 rounded-2xl bg-[hsl(234,89%,63%)]/10 flex items-center justify-center mx-auto mb-4">
                    <Upload className="w-7 h-7 text-[hsl(234,89%,63%)]" />
                  </div>
                  <p className="text-lg font-medium text-[hsl(222,47%,11%)] mb-2">
                    Drag & drop your artwork here
                  </p>
                  <p className="text-slate-500 mb-4">
                    or click to browse files
                  </p>
                  <p className="text-xs text-slate-400">
                    Supports JPEG, PNG, WebP • Max 1 image • Max 10MB
                  </p>
                </div>

                {/* Uploaded Images Preview */}
                {uploadedImages.length > 0 && (
                  <div className="mt-6" data-testid="uploaded-images">
                    <p className="text-sm font-medium text-slate-700 mb-3">
                      Uploaded ({uploadedImages.length}/1)
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                      {uploadedImages.map((image) => (
                        <div key={image.id} className="relative group" data-testid={`uploaded-image-${image.id}`}>
                          <div className="aspect-square rounded-xl overflow-hidden border border-slate-200">
                            <img
                              src={image.preview}
                              alt={image.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeImage(image.id);
                            }}
                            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[hsl(12,95%,65%)] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                            data-testid={`remove-image-${image.id}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                      {/* Single artwork limit — no 'add more' button */}
                    </div>
                  </div>
                )}

                {/* Analyze Button */}
                {uploadedImages.length > 0 && !isAnalyzing && (
                  <Button
                    onClick={handleAnalyze}
                    className="w-full mt-6 bg-[hsl(234,89%,63%)] hover:bg-[hsl(234,89%,58%)] text-white rounded-full h-11"
                    data-testid="analyze-button"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Analyze My Artwork
                  </Button>
                )}

                {/* Analyzing State */}
                {isAnalyzing && !isGeneratingRoadmap && (
                  <div className="mt-6 text-center" data-testid="analyzing-state">
                    <div className="w-12 h-12 rounded-full bg-[hsl(234,89%,63%)]/10 flex items-center justify-center mx-auto mb-4 animate-pulse">
                      <Sparkles className="w-6 h-6 text-[hsl(234,89%,63%)]" />
                    </div>
                    <p className="text-base font-medium text-[hsl(222,47%,11%)] mb-2">Analyzing your artwork...</p>
                    <p className="text-slate-500 text-sm mb-4">AI is evaluating composition, technique, and style</p>
                    <Progress value={66} className="h-2 max-w-xs mx-auto animate-pulse" />
                  </div>
                )}

                {/* Generating Roadmap State */}
                {isGeneratingRoadmap && (
                  <div className="mt-6 text-center" data-testid="generating-roadmap-state">
                    <div className="w-16 h-16 relative mx-auto mb-4">
                      <div className="absolute inset-0 rounded-full border-4 border-slate-200"></div>
                      <div className="absolute inset-0 rounded-full border-4 border-[hsl(234,89%,63%)] border-t-transparent animate-spin"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Sparkles className="w-6 h-6 text-[hsl(234,89%,63%)] animate-pulse" />
                      </div>
                    </div>
                    <p className="text-base font-medium text-[hsl(222,47%,11%)] mb-2">Generating Personalized Curriculum...</p>
                    <p className="text-slate-500 text-sm mb-4">Roadmap generation may take 2-3 minutes, you could organise your art materials in the mean time</p>
                    <Progress value={100} className="h-2 max-w-xs mx-auto animate-pulse" />
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Analysis Results */}
          {analysisError && !analysisComplete && (
            <div className="max-w-xl mx-auto mt-6 text-center text-sm text-red-500" data-testid="upload-error">
              {analysisError}
            </div>
          )}

          {analysisComplete && analysisData && (
            <div className="space-y-5 animate-fade-in" data-testid="analysis-results">
              {/* Overall Score */}
              <Card className="card-elevated border-0 overflow-hidden">
                <div className="bg-gradient-to-r from-[hsl(234,89%,63%)] to-[hsl(262,83%,58%)] p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white/75 text-sm font-medium mb-1">Skill Gap Report</p>
                      <h2 className="text-4xl font-bold font-['Outfit']">{analysisData.overallScore}/100</h2>
                      <Badge className="bg-white/15 text-white mt-2">{analysisData.skillLevel} Level</Badge>
                    </div>
                    <div className="w-20 h-20 rounded-full bg-white/15 flex items-center justify-center">
                      <CheckCircle2 className="w-10 h-10 text-white" />
                    </div>
                  </div>
                </div>
                <CardContent className="p-5">
                  <p className="text-slate-600 text-sm">
                    Based on your uploaded reference art, we've identified your skill gaps and will generate a Dynamic Roadmap tailored to your goals.
                  </p>
                </CardContent>
              </Card>

              {/* Skill Metrics Breakdown */}
              <Card className="card-elevated border-0" data-testid="category-scores">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-['Outfit'] flex items-center gap-2">
                    <Eye className="w-5 h-5 text-[hsl(234,89%,63%)]" />
                    Skill Metrics (1-10 Scale)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {analysisData.categories.map((category, idx) => (
                    <div key={idx} className="p-4 rounded-xl bg-slate-50 border border-slate-100" data-testid={`category-${idx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-[hsl(222,47%,11%)] text-sm">{category.name}</span>
                        <span className="text-[hsl(234,89%,63%)] font-semibold">{Math.round(category.score / 10)}/10</span>
                      </div>
                      <Progress value={category.score} className="h-1.5 mb-2" />
                      <p className="text-xs text-slate-600">{category.feedback}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Strengths & Improvements */}
              <div className="grid md:grid-cols-2 gap-5">
                <Card className="card-elevated border-0" data-testid="strengths-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-['Outfit'] flex items-center gap-2">
                      <Palette className="w-4 h-4 text-[hsl(142,76%,36%)]" />
                      Identified Strengths
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {Array.isArray(analysisData.strengths) && analysisData.strengths.length > 0 ? (
                      <ul className="space-y-2">
                        {analysisData.strengths.map((strength, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                            <CheckCircle2 className="w-4 h-4 text-[hsl(142,76%,36%)] shrink-0 mt-0.5" />
                            {strength}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-slate-600">
                        We’ll highlight specific strengths here as your roadmap evolves.
                      </p>
                    )}
                  </CardContent>
                </Card>

                <Card className="card-elevated border-0" data-testid="improvements-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-['Outfit'] flex items-center gap-2">
                      <Lightbulb className="w-4 h-4 text-[hsl(12,95%,65%)]" />
                      Areas for Improvement
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {Array.isArray(analysisData.improvements) && analysisData.improvements.length > 0 ? (
                      <ul className="space-y-2">
                        {analysisData.improvements.map((improvement, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                            <Layers className="w-4 h-4 text-[hsl(12,95%,65%)] shrink-0 mt-0.5" />
                            {improvement}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-slate-600">
                        We’ll surface targeted improvement suggestions as you complete more exercises.
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Continue Button */}
              <Card className="bg-[hsl(222,47%,11%)] border-0 shadow-xl">
                <CardContent className="p-8 text-center">
                  <h3 className="text-xl font-bold text-white mb-2 font-['Outfit']">
                    Your Dynamic Roadmap is Ready!
                  </h3>
                  <p className="text-slate-300 mb-6 max-w-lg mx-auto text-sm">
                    Based on your Skill Gap Report, we've generated a personalized curriculum with 15, 30, and 60-minute exercises targeting your specific needs.
                  </p>
                  <Button
                    onClick={handleContinue}
                    className="bg-white text-[hsl(222,47%,11%)] hover:bg-slate-100 rounded-full h-11 px-8"
                    data-testid="continue-to-dashboard"
                  >
                    View My Curriculum
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
